import type { AdditionalChainSection } from '../additionalChainUtils';
import AdditionalChainAddCard from './AdditionalChainAddCard';
import BoneAssignmentCategorySection from './BoneAssignmentCategorySection';
import AdditionalChainDiagramCard from './AdditionalChainDiagramCard';

interface AdditionalChainDiagramSectionProps {
  section: AdditionalChainSection;
  activeChainName: string | null;
  focusedChainName: string | null;
  hoveredChainName: string | null;
  pendingChainName: string | null;
  chainAssignmentEnabled: boolean;
  onHoveredChainChange: (chainName: string | null) => void;
  onChainClick: (chainName: string) => void;
  onChainClear?: (chainNames: readonly string[]) => void;
  onAddChain: (chainName: string) => void;
}

export default function AdditionalChainDiagramSection({
  section,
  activeChainName,
  focusedChainName,
  hoveredChainName,
  pendingChainName,
  chainAssignmentEnabled,
  onHoveredChainChange,
  onChainClick,
  onChainClear,
  onAddChain,
}: AdditionalChainDiagramSectionProps) {
  return (
    <BoneAssignmentCategorySection
      title={section.label}
      defaultOpen={section.defaultOpen}
    >
      <div className="flex flex-col gap-3 pt-3">
        {section.families.map((familyGroup) => (
          <div key={familyGroup.family.key}>
            <div className="grid grid-cols-2 gap-1.5">
              {familyGroup.instances.map((instance) => (
                <AdditionalChainDiagramCard
                  key={instance.diagram.key}
                  instance={instance}
                  activeChainName={activeChainName}
                  focusedChainName={focusedChainName}
                  hoveredChainName={hoveredChainName}
                  pendingChainName={pendingChainName}
                  onHoveredChainChange={onHoveredChainChange}
                  onChainClick={onChainClick}
                  onChainClear={onChainClear}
                />
              ))}
              {familyGroup.nextChainName && (
                <AdditionalChainAddCard
                  familyLabel={familyGroup.family.label}
                  chainName={familyGroup.nextChainName}
                  disabled={!chainAssignmentEnabled}
                  onAddChain={onAddChain}
                />
              )}
            </div>
          </div>
        ))}
        {section.subsections.map((subsection) => {
          const subsectionChainNames = subsection.families.flatMap((familyGroup) =>
            familyGroup.instances.flatMap((instance) =>
              instance.members.map((member) => member.chainName),
            ),
          );
          const subsectionDefaultOpen =
            subsection.defaultOpen ||
            (focusedChainName !== null &&
              subsectionChainNames.includes(focusedChainName)) ||
            (pendingChainName !== null &&
              subsectionChainNames.includes(pendingChainName));

          return (
            <BoneAssignmentCategorySection
              key={`${section.key}-${subsection.key}`}
              title={subsection.label}
              defaultOpen={subsectionDefaultOpen}
            >
              <div className="flex flex-col gap-3 pt-3">
                {subsection.families.map((familyGroup) => (
                  <div key={familyGroup.family.key}>
                    <div className="grid grid-cols-2 gap-1.5">
                      {familyGroup.instances.map((instance) => (
                        <AdditionalChainDiagramCard
                          key={instance.diagram.key}
                          instance={instance}
                          activeChainName={activeChainName}
                          focusedChainName={focusedChainName}
                          hoveredChainName={hoveredChainName}
                          pendingChainName={pendingChainName}
                          onHoveredChainChange={onHoveredChainChange}
                          onChainClick={onChainClick}
                          onChainClear={onChainClear}
                        />
                      ))}
                      {familyGroup.nextChainName && (
                        <AdditionalChainAddCard
                          familyLabel={familyGroup.family.label}
                          chainName={familyGroup.nextChainName}
                          disabled={!chainAssignmentEnabled}
                          onAddChain={onAddChain}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </BoneAssignmentCategorySection>
          );
        })}
      </div>
    </BoneAssignmentCategorySection>
  );
}
